import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiSearch, FiDownload, FiFileText, FiRefreshCw } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { getReportDef } from '../../config/reports';
import { exportToExcel, exportToPDF, exportInvoiceSummaryExcel } from '../../utils/export';
import { formatMoney, unwrapList } from '../../utils/format';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import Loader from '../../components/common/Loader';
import SearchableSelect from '../../components/common/SearchableSelect';
import { showError } from '../../utils/alert';

const defaultFilters = (def) => {
  const out = {};
  def.filters.forEach((f) => {
    if (f.type === 'date') out[f.key] = '';
    else if (f.type === 'month') out[f.key] = new Date().toISOString().slice(0, 7);
    else out[f.key] = '';
  });
  return out;
};

export default function ReportPage() {
  const { reportKey } = useParams();
  const def = getReportDef(reportKey);

  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!def) return;
    setFilters(defaultFilters(def));
    setData(null);
  }, [def]);

  useEffect(() => {
    if (!def) return;
    api.parties.list({ per_page: 500 }).then(({ data: d }) => {
      setParties(unwrapList(d, 'parties'));
    }).catch(() => {});
    api.inventory.items({ per_page: 500 }).then(({ data: d }) => {
      setItems(unwrapList(d, 'items'));
    }).catch(() => {});
  }, [def]);

  const load = useCallback(async () => {
    if (!def) return;
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v != null) params[k] = v;
    });
    if (def.needsParty && !params.party_id) {
      showError('Please select a party');
      return;
    }
    setLoading(true);
    try {
      const { data: res } = await def.endpoint(params);
      setData(res);
    } catch (err) {
      showError('Failed to load report', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [def, filters]);

  useEffect(() => {
    if (def) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def]);

  if (!def) {
    return (
      <div className="page">
        <div className="empty-state"><h4>Report not found</h4></div>
      </div>
    );
  }

  const summary = (data && (data.summary || data)) || {};
  const tableData = data ? data : [];
  const listRef = tableData[def.key] || (tableData.logs && tableData.logs.data ? tableData.logs : null) || null;
  const rows = Array.isArray(tableData)
    ? tableData
    : tableData.data
      ? tableData.data
      : tableData[def.key] && tableData[def.key].data
        ? tableData[def.key].data
        : tableData[def.key]
          ? Array.isArray(tableData[def.key]) ? tableData[def.key] : []
          : tableData.attendance
            ? tableData.attendance
            : tableData.machines
              ? tableData.machines
              : tableData.items
                ? tableData.items
                : tableData.entries
                  ? tableData.entries
                  : tableData.logs && tableData.logs.data
                    ? tableData.logs.data
                    : tableData.grouped
                      ? (tableData.grouped.data || (Array.isArray(tableData.grouped) ? tableData.grouped : []))
                      : [];

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const excelColumns = def.columns.map((c) => ({ key: c.key, label: c.label }));

  const handleExcel = () => {
    if (typeof def.customExcel === 'function') def.customExcel(rows, def.fileName);
    else if (def.customExcel) exportInvoiceSummaryExcel(rows, def.fileName);
    else exportToExcel(rows, def.fileName, excelColumns);
  };

  return (
    <div className="page">
      <PageHeader
        title={def.label}
        subtitle="View, filter and export the report."
        crumbs={[{ label: 'Reports', to: '/reports' }, { label: def.label }]}
        actions={
          <>
            <button className="btn btn-secondary" onClick={load}><FiRefreshCw /> Refresh</button>
            <button
              className="btn btn-secondary"
              onClick={() => exportToPDF(rows, def.fileName, excelColumns)}
              disabled={!rows.length}
            >
              <FiFileText /> PDF
            </button>
            <button
              className="btn btn-success"
              onClick={handleExcel}
              disabled={!rows.length}
            >
              <FiDownload /> Export Excel
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <div className="filters-bar">
            <FiSearch style={{ color: 'var(--text-muted)' }} />
            {def.filters.map((f) => (
              <div key={f.key}>
                {f.type === 'select' && (
                  <SearchableSelect options={[{ value: '', label: `${f.label} (All)` }, ...f.options.map((o) => ({ value: o, label: o }))]} value={filters[f.key] || ''} onChange={(v) => setFilter(f.key, v)} placeholder={`${f.label} (All)`} />
                )}
                {f.type === 'date' && <input className="form-control" type="date" value={filters[f.key] || ''} onChange={(e) => setFilter(f.key, e.target.value)} placeholder={f.label} />}
                {f.type === 'month' && <input className="form-control" type="month" value={filters[f.key] || ''} onChange={(e) => setFilter(f.key, e.target.value)} />}
                {f.type === 'text' && <input className="form-control" placeholder={f.label} value={filters[f.key] || ''} onChange={(e) => setFilter(f.key, e.target.value)} />}
                {f.type === 'party' && (
                  <SearchableSelect options={parties} value={filters.party_id || ''} onChange={(v) => setFilter('party_id', v)} placeholder="-- Select party --" />
                )}
                {f.type === 'item' && (
                  <SearchableSelect options={items} value={filters.item_id || ''} onChange={(v) => setFilter('item_id', v)} placeholder="-- Select item --" />
                )}
              </div>
            ))}
            <button className="btn btn-primary btn-sm" onClick={() => load()}><FiRefreshCw /> Apply</button>
          </div>

          {def.summaryKeys.length > 0 && (
            <div className="stats-grid" style={{ marginBottom: 18 }}>
              {def.summaryKeys.map((s) => (
                <div key={s.key} className="stat-card">
                  <div className="stat-icon indigo" style={{ fontSize: 18 }}><i className={s.icon || 'bi bi-bar-chart-fill'} /></div>
                  <div>
                    <div className="stat-value">{s.money ? formatMoney(summary[s.key]) : summary[s.key] ?? '-'}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {loading ? (
            <Loader text="Loading report..." />
          ) : (
            <DataTable
              data={rows}
              emptyTitle="No data for selected filters"
              emptyMessage="Try changing the filter criteria."
              columns={def.columns}
            />
          )}
        </div>
      </div>

      <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-muted)' }}>
        <Link to="/reports">← Back to all reports</Link>
      </div>
    </div>
  );
}